import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { getUserLanguage } from '../utils/language';
import { useCart } from '@context/CartContext';
import { Link } from 'react-router-dom';
import { post } from '../utils/api'; // Import post utility

const Checkout = () => {
  const language = getUserLanguage();
  const { cart, clearCart } = useCart();
  const [shippingMethods, setShippingMethods] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    shippingMethod: '',
    paymentMethod: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [formattedOrderId, setFormattedOrderId] = useState(null);
  const [orderTotal, setOrderTotal] = useState(0);
  const { ref: sectionRef, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const shippingResponse = await fetch("https://api.norisknodrift.com/api/orders/shipping-methods", {
          headers: { "accept-language": language },
        });
        if (!shippingResponse.ok) {
          throw new Error(`HTTP error! Status: ${shippingResponse.status}`);
        }
        const shipping = await shippingResponse.json();

        const paymentResponse = await fetch("https://api.norisknodrift.com/api/orders/payment-methods", {
          headers: { "accept-language": language },
        });
        if (!paymentResponse.ok) {
          throw new Error(`HTTP error! Status: ${paymentResponse.status}`);
        }
        const payment = await paymentResponse.json();

        console.log('[Checkout] Shipping methods:', shipping);
        console.log('[Checkout] Payment methods:', payment);
        setShippingMethods(Array.isArray(shipping) ? shipping : []);
        setPaymentMethods(Array.isArray(payment) ? payment : []);
      } catch (err) {
        console.error('[Checkout] Fetch options error:', err.message);
        setError(
          language === 'cs'
            ? 'Nepodařilo se načíst možnosti dopravy a platby: ' + (err.message || '')
            : 'Failed to load shipping and payment options: ' + (err.message || '')
        );
      }
    };
    fetchOptions();
  }, [language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getShippingPrice = () => {
    const selectedMethod = shippingMethods.find(
      (method) => method.id === parseInt(formData.shippingMethod)
    );
    return selectedMethod ? parseFloat(selectedMethod.price) : 0;
  };

  const calculateTotal = () => {
    const itemsTotal = cart.reduce(
      (total, item) => total + parseFloat(item.price) * item.quantity,
      0
    );
    const shippingPrice = getShippingPrice();
    return itemsTotal + shippingPrice;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const total = calculateTotal();
    setOrderTotal(total);

    // Připravíme položky objednávky s číselným formátem pro price
    const cleanedItems = cart.map(item => ({
      id: item.id,
      quantity: item.quantity,
      price: parseFloat(item.price), // Konvertujeme na číslo
      translations: {
        en: { name: item.translations?.en?.name || '', description: item.translations?.en?.description || '' },
        cs: { name: item.translations?.cs?.name || '', description: item.translations?.cs?.description || '' }
      }
    }));

    try {
      const orderData = {
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: `${formData.address}, ${formData.city}, ${formData.postalCode}`,
        },
        items: cleanedItems,
        shipping_method: parseInt(formData.shippingMethod),
        payment_method: parseInt(formData.paymentMethod),
        total: total,
      };
      console.log('[Checkout] Submitting order:', JSON.stringify(orderData, null, 2));

      const headers = { 'accept-language': language };
      const responseData = await post('/orders', orderData, headers);
      console.log('[Checkout] Order response:', responseData);

      const rawOrderId = responseData.orderId || 'N/A';
      setOrderId(rawOrderId);

      const paddedOrderId = rawOrderId.toString().padStart(10, '0');
      setFormattedOrderId(paddedOrderId);

      setSuccess(true);
      clearCart();
    } catch (err) {
      console.error('[Checkout] Submit order error:', err.message, err.stack);
      setError(
        language === 'cs'
          ? 'Objednávku se nepodařilo odeslat: ' + (err.message || 'Neznámá chyba')
          : 'Failed to submit order: ' + (err.message || 'Unknown error')
      );
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !success) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Dokončení objednávky' : 'Checkout'} />
        <div className="text-center text-white py-20">
          <p>{language === 'cs' ? 'Váš košík je prázdný.' : 'Your cart is empty.'}</p>
          <Link to="/shop" className="btn-drift mt-4 inline-block">
            {language === 'cs' ? 'Přejít do e-shopu' : 'Go to Shop'}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={language === 'cs' ? 'Dokončení objednávky' : 'Checkout'} />
      <section className="py-20 bg-dark-gray relative" ref={sectionRef}>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {success ? (
            <div className="drift-card p-6">
              <div className="text-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-4 text-white text-center">
                {language === 'cs' ? 'Děkujeme za vaši objednávku!' : 'Thank you for your order!'}
              </h2>
              <p className="text-xl mb-2 text-white text-center">
                {language === 'cs' ? 'Číslo objednávky:' : 'Order number:'} <span className="font-bold">#{formattedOrderId || orderId}</span>
              </p>
              <p className="mb-6 text-white text-center">
                {language === 'cs'
                  ? 'Zálohová faktura vám byla odeslána na email.'
                  : 'An invoice has been sent to your email.'}
              </p>
              
              {formData.paymentMethod && (
                <div className="bg-accent-gray p-4 rounded-lg mb-6 max-w-md mx-auto text-left">
                  <h3 className="text-xl font-bold mb-3 text-white text-center red-accent">
                    {language === 'cs' ? 'Platební údaje' : 'Payment details'}
                  </h3>
                  {paymentMethods.find(method => method.id.toString() === formData.paymentMethod)?.name.toLowerCase().includes('bank') && (
                    <>
                      <div className="mb-2">
                        <span className="text-gray-400">{language === 'cs' ? 'Bankovní účet:' : 'Bank account:'}</span>
                        <span className="block font-bold text-white">1234567890/1000</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-400">{language === 'cs' ? 'Variabilní symbol:' : 'Variable symbol:'}</span>
                        <span className="block font-bold text-white">{formattedOrderId || orderId}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-400">{language === 'cs' ? 'Částka:' : 'Amount:'}</span>
                        <span className="block font-bold text-xl text-white">
                          {new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-US').format(orderTotal)}{' '}
                          {language === 'cs' ? 'Kč' : 'CZK'}
                        </span>
                      </div>
                      <div className="mb-0">
                        <span className="text-gray-400">{language === 'cs' ? 'Splatnost:' : 'Due date:'}</span>
                        <span className="block font-bold text-white">
                          {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(
                            language === 'cs' ? 'cs-CZ' : 'en-US'
                          )}
                        </span>
                      </div>
                    </>
                  )}
                  {paymentMethods.find(method => method.id.toString() === formData.paymentMethod)?.name.toLowerCase().includes('card') && (
                    <p className="text-center text-white">
                      {language === 'cs' 
                        ? 'Platba kartou byla zpracována. Děkujeme za vaši platbu.' 
                        : 'Card payment has been processed. Thank you for your payment.'}
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                <Link to="/shop" className="btn-drift">
                  {language === 'cs' ? 'Zpět do e-shopu' : 'Back to Shop'}
                </Link>
                <a 
                  href={`mailto:info@norisknodrift.com?subject=${encodeURIComponent(
                    language === 'cs' 
                      ? `Dotaz k objednávce #${formattedOrderId || orderId}` 
                      : `Question about order #${formattedOrderId || orderId}`
                  )}`} 
                  className="btn-outline"
                >
                  {language === 'cs' ? 'Mám dotaz k objednávce' : 'I have a question'}
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="drift-card p-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                {language === 'cs' ? 'Dokončení objednávky' : 'Complete Your Order'}
              </h2>
              {error && <p className="text-red-500 mb-4">{error}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'Jméno' : 'Name'}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'Telefon' : 'Phone'}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'Adresa' : 'Address'}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'Město' : 'City'}
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'PSČ' : 'Postal Code'}
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'Doprava' : 'Shipping'}
                  </label>
                  <select
                    name="shippingMethod"
                    value={formData.shippingMethod}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  >
                    <option value="">
                      {language === 'cs' ? 'Vyberte dopravu' : 'Select shipping'}
                    </option>
                    {shippingMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name} (
                        {new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-US').format(method.price)}{' '}
                        {language === 'cs' ? 'Kč' : 'CZK'})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white mb-2">
                    {language === 'cs' ? 'Platba' : 'Payment'}
                  </label>
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    className="w-full bg-black border border-gray-700 text-white p-2 rounded"
                    required
                  >
                    <option value="">
                      {language === 'cs' ? 'Vyberte platbu' : 'Select payment'}
                    </option>
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6 text-right">
                <p className="text-xl text-white mb-4">
                  {language === 'cs'
                    ? 'Celkem (včetně dopravy):'
                    : 'Total (including shipping):'}{' '}
                  <span className="font-bold">
                    {new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-US').format(calculateTotal())}{' '}
                    {language === 'cs' ? 'Kč' : 'CZK'}
                  </span>
                </p>
                <button type="submit" className="btn-drift" disabled={loading}>
                  {loading ? (
                    <span>{language === 'cs' ? 'Odesílání...' : 'Submitting...'}</span>
                  ) : (
                    <span>{language === 'cs' ? 'Odeslat objednávku' : 'Submit Order'}</span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Checkout;